/**
 * The small parts of Walk it through on a phone and an iPad: the three dots, the rows under a preview, and the sheets
 * that open from them. Every row and field reads the card the computer's walk reads.
 */
import { type ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { BOARD_CAPS } from "../../../shared/board-caps";
import type { BoardState } from "../../../shared/types";
import { GroupedList, ListRow } from "../../components/season/GroupedList";
import { Button } from "../../components/ui/Button";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Sheet } from "../../components/ui/Sheet";
import { Toggle } from "../../components/ui/Toggle";
import { cx } from "../../components/ui/cx";
import { flatten, graphToTree, type Step } from "../../automations/flow-model";
import { plainLine } from "../../automations/words";
import { kindIcon, waitWords } from "../../components/FlowEditor";
import type { BoardHost } from "../host";
import { mmss } from "../make/time";
import { flipFollowUp, followName, type Mail } from "../walk/followRows";
import { KIND, type Moment, momentWords, type Offer } from "../walk/roomParts";
import { offerNeedsLink } from "../../../shared/offer";
import { lostChanges, STEP_NAMES, WALK_STEPS, type WalkStep } from "../walk/useWalk";
import { OFFER_KINDS } from "../walk/WalkFields";
import { usePublishFacts } from "../walk/publishFacts";
import type { TappedSlot } from "./SeasonPageFrame";

// --- the dots ---

/** The stops as dots, each a 44 px target; the one on is wine, the ones looked at are half wine. */
export function StopDots({ step, looked, onGo }: { step: WalkStep; looked: ReadonlySet<string>; onGo: (s: WalkStep) => void }) {
  return (
    <div className="mk-dotsrow" role="group" aria-label="The steps">
      {WALK_STEPS.map((s, i) => (
        <button
          type="button"
          key={s}
          className={cx("mk-dot", s === step ? "on" : looked.has(s) && "ok")}
          aria-label={`${i + 1} of ${WALK_STEPS.length}, ${STEP_NAMES[s]}`}
          {...(s === step ? { "aria-current": "step" as const } : {})}
          onClick={() => onGo(s)}
        >
          <i />
        </button>
      ))}
    </div>
  );
}

/** The stops as a list, for the iPad on its side. */
export function StopList({ step, looked, followCount, onGo }: { step: WalkStep; looked: ReadonlySet<string>; followCount: number; onGo: (s: WalkStep) => void }) {
  return (
    <GroupedList>
      {WALK_STEPS.map((s, i) => (
        <ListRow
          key={s}
          on={s === step}
          noChevron
          lead={
            <span className={cx("k-ico", looked.has(s) ? "good" : s === step && "wine")}>
              {looked.has(s) ? <Icon name="check" /> : <b>{i + 1}</b>}
            </span>
          }
          title={STEP_NAMES[s]}
          sub={s === "followups" ? `${followCount} ${followCount === 1 ? "follow-up" : "follow-ups"}` : undefined}
          onClick={() => onGo(s)}
        />
      ))}
    </GroupedList>
  );
}

// --- the rows under the room ---

export function MomentRows({ moments, live, shown, onShow }: { moments: Moment[]; live: boolean; shown: string | null; onShow: (id: string) => void }) {
  return (
    <GroupedList>
      {moments.map((m) => (
        <ListRow
          key={m.id}
          icon={KIND[m.kind].icon}
          on={m.id === shown}
          title={<span className="k-clamp">{momentWords(m)}</span>}
          sub={`${KIND[m.kind].name} · ${live ? `About minute ${Math.round((m.t ?? 0) / 60)}` : `At ${mmss(m.t ?? 0)}`}`}
          onClick={() => onShow(m.id)}
        />
      ))}
    </GroupedList>
  );
}

/** Three rows that open the offer's fields, each at the field it names. */
export function OfferRows({ offer, live, onOpen }: { offer: Offer; live: boolean; onOpen: (focus: string) => void }) {
  const kind = offer.offerType;
  const row = (label: string, value: string, focus: string) => (
    <ListRow key={focus} title={<span className="k-k">{label}</span>} end={<span className="k-v">{value}</span>} onClick={() => onOpen(focus)} />
  );
  const opens =
    kind === "call" || kind === "buy"
      ? row("The button opens", offer.on && offerNeedsLink(offer) ? "No link yet" : offer.url.replace(/^https?:\/\//, ""), "The button opens")
      : kind === "code"
        ? row("Where to use it", offer.url.replace(/^https?:\/\//, ""), "Where to use it")
        : kind === "download"
          ? row("File", offer.file ? (offer.file.split("/").pop() ?? "") : "None yet", "File")
          : null;
  const at = live ? `About minute ${Math.round((offer.t ?? 0) / 60)}` : `At ${mmss(offer.t ?? 0)}`;
  return (
    <GroupedList className="k-kv mk-kvl">
      {row("When they press it", OFFER_KINDS.find((x) => x.type === kind)?.label ?? "", "When they press it")}
      {opens}
      {row("Shows", `${at}${typeof offer.showFor === "number" ? ", two minutes" : ", to the end"}`, "Shows")}
    </GroupedList>
  );
}

// --- follow-ups ---

/** A follow-up's plain line: when it starts and what it does, as the Automations list says it. */
export function followLine(host: BoardHost, m: Mail): string {
  const row = m.automationId ? host.followUpRows.get(m.automationId) : undefined;
  if (!m.on) return "Off for this room";
  return row ? plainLine(row) : m.subject || "No subject yet";
}

export function FollowRows({ mails, host, onOpen, onAdd, busy }: { mails: Mail[]; host: BoardHost; onOpen: (m: Mail) => void; onAdd: () => void; busy: boolean }) {
  return (
    <>
      <ul className="k-gl" id="mk-fl">
        {mails.map((m) => (
          <li key={m.id} className={cx("k-lr", !m.on && "mk-off")}>
            <div className="k-lr-b mk-fr">
              <button type="button" className="mk-frb" onClick={() => onOpen(m)}>
                <span className="k-ico">
                  <Icon name="zap" />
                </span>
                <span className="k-lr-t">
                  <b>{followName(host, m)}</b>
                  <span>{followLine(host, m)}</span>
                </span>
              </button>
              <Toggle on={m.on} onChange={() => flipFollowUp(host, m)} label={`${followName(host, m)}, on for this room`} />
            </div>
          </li>
        ))}
      </ul>
      <button type="button" className="k-fold mk-add" disabled={busy} onClick={onAdd}>
        <span className="k-fold-t">
          <b>{busy ? "Writing it…" : "Add a follow-up"}</b>
        </span>
        <span className="k-fold-c">
          <Icon name="plus" />
        </span>
      </button>
    </>
  );
}

function stepLine(s: Step): [IconName, string] {
  switch (s.type) {
    case "email":
      return [kindIcon("email"), `Email: ${s.subject.trim() || "not written yet"}`];
    case "room":
      return [kindIcon("room"), `Add to their page: ${s.title.trim() || "no title yet"}`];
    case "fact":
      return [kindIcon("fact"), `${s.op === "remove" ? "Take off" : "Add"} the tag ${s.name}`];
    case "webhook":
      return [kindIcon("webhook"), `Send to ${s.url.trim() || "an address not set yet"}`];
    case "notify":
      return [kindIcon("notify"), `Tell you: ${s.subject.trim() || "an update"}`];
    case "wait":
      return [kindIcon("wait"), waitWords(s, true)];
    case "branch":
      return [kindIcon("branch"), "A check"];
  }
}

/** What a follow-up does, step by step, from its own automation. */
export function FollowSteps({ host, m }: { host: BoardHost; m: Mail }) {
  const row = m.automationId ? host.followUpRows.get(m.automationId) : undefined;
  const steps = row ? flatten(graphToTree(row.graph)) : [];
  return (
    <>
      <h2 className="k-gh">What it does</h2>
      {steps.length ? (
        <ul className="k-gl k-nolead">
          {steps.map((s) => {
            const [icon, words] = stepLine(s);
            return (
              <li className="k-lr" key={s.uid}>
                <div className="k-lr-b">
                  <span className="k-ico">
                    <Icon name={icon} />
                  </span>
                  <span className="k-lr-t">
                    <b>{words}</b>
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="k-sub">No steps yet.</p>
      )}
    </>
  );
}

/** A follow-up's start, its switch for this room and what it does; the steps are edited in Follow-ups. */
export function FollowSheet({ host, m, onClose }: { host: BoardHost; m: Mail; onClose: () => void }) {
  const row = m.automationId ? host.followUpRows.get(m.automationId) : undefined;
  return (
    <Sheet
      open
      onClose={onClose}
      title={followName(host, m)}
      footer={
        <>
          <Button onClick={onClose}>Done</Button>
          {m.automationId ? (
            <Link className="btn p" to={`/automations/${m.automationId}`}>
              <Icon name="zap" /> Edit the steps in Follow-ups
            </Link>
          ) : null}
        </>
      }
    >
      <ul className="k-gl k-kv k-nolead">
        <li className="k-lr">
          <div className="k-lr-b">
            <span className="k-k">When someone gets the tag</span>
            <span className="k-v">{row ? row.trigger.value : ""}</span>
          </div>
        </li>
        <li className="k-lr">
          <div className="k-lr-b mk-onr">
            <span className="k-k">On for this room</span>
            <Toggle on={m.on} onChange={() => flipFollowUp(host, m)} label={`${followName(host, m)}, on for this room`} />
          </div>
        </li>
      </ul>
      <FollowSteps host={host} m={m} />
    </Sheet>
  );
}

// --- sheets for the page ---

const SLOT_LABELS: Record<string, string> = {
  title: "The headline",
  description: "The description",
  sub: "The description",
  "host-name": "Your name",
  "host-line": "About you",
  bio: "About you",
  quote: "The quote",
  chapter: "A chapter’s name",
  "faq-q": "A question",
  "faq-a": "Its answer",
  learn: "What you will learn",
  heading: "The heading",
  "offer-title": "The offer’s headline",
  "offer-text": "The offer’s words",
  "offer-button": "The offer’s button",
};

/** A slot's field, named after what the guest sees there. */
export function slotLabel(slot: string): string {
  return SLOT_LABELS[slot] ?? "These words";
}

/** One slot's words in a field of their own. A "What you will learn" line can go out while others stay. */
export function TextSheet({ tapped, onSave, onOut, onClose }: { tapped: TappedSlot; onSave: (text: string) => void; onOut: () => void; onClose: () => void }) {
  const [text, setText] = useState(tapped.text);
  const label = slotLabel(tapped.slot);
  const long = tapped.text.length > 50 || ["description", "sub", "bio", "faq-a", "offer-text"].includes(tapped.slot);
  const canOut = tapped.slot === "learn" && tapped.count > 1;
  return (
    <Sheet
      open
      onClose={onClose}
      title={label}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="check" disabled={!text.trim()} onClick={() => onSave(text.trim())}>
            Save
          </Button>
        </>
      }
    >
      <div className="field mk-f">
        {long ? (
          <textarea dir="auto" className="inp" rows={5} aria-label={label} value={text} maxLength={BOARD_CAPS.prose} onChange={(e) => setText(e.target.value)} />
        ) : (
          <input dir="auto" className="inp" aria-label={label} value={text} maxLength={BOARD_CAPS.prose} onChange={(e) => setText(e.target.value)} />
        )}
      </div>
      {canOut ? (
        <button type="button" className="k-link mk-out" onClick={onOut}>
          Take this line out
        </button>
      ) : null}
    </Sheet>
  );
}

/** The page's address: the part before the slash is the business's, the rest is typed. */
export function AddressSheet({ base, slug, onSave, onClose }: { base: string; slug: string; onSave: (slug: string) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(slug);
  const clean = draft.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
  return (
    <Sheet
      open
      onClose={onClose}
      title="The address"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="check" disabled={!clean} onClick={() => onSave(clean)}>
            Save
          </Button>
        </>
      }
    >
      <div className="field mk-f">
        <span className="lbl">The page’s address</span>
        <span className="mk-addr">
          <span className="mono">{base}/</span>
          <input dir="auto" className="inp mono" aria-label="The page’s address" value={draft} spellCheck={false} autoCapitalize="off" onChange={(e) => setDraft(e.target.value)} />
        </span>
      </div>
    </Sheet>
  );
}

/** A group of fields in a sheet, with Done. */
export function FieldsSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <Sheet
      open
      onClose={onClose}
      title={title}
      footer={
        <Button variant="p" icon="check" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="k-form mk-ff">{children}</div>
    </Sheet>
  );
}

// --- "..." and starting over ---

export function StartOverSheet({ live, threads, onClose, onStart }: { live: boolean; threads: Parameters<typeof lostChanges>[0]; onClose: () => void; onStart: () => void }) {
  return (
    <Sheet
      open
      onClose={onClose}
      title={live ? "Start over from your notes?" : "Start over from the talk?"}
      sub="We write a new board from the start. There’s no undo."
      footer={
        <>
          <Button onClick={onClose}>Keep my board</Button>
          <Button variant="p" onClick={onStart}>
            Start over
          </Button>
        </>
      }
    >
      <div className="nbcost">
        {lostChanges(threads).map(([n, words]) => (
          <div key={words}>
            <b>{n}</b>
            <span>{words}</span>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// --- publish ---

/** What Publish checks: what blocks it, the steps looked at and not, and the emails waiting for the business's email. */
export function PublishSheet({
  state,
  host,
  looked,
  address,
  onClose,
  onGo,
  onGoOffer,
}: {
  state: BoardState;
  host: BoardHost;
  looked: ReadonlySet<string>;
  address: string;
  onClose: () => void;
  onGo: (s: WalkStep) => void;
  onGoOffer: () => void;
}) {
  const { blocks, waiting, seen, title, canEdit } = usePublishFacts(state, host, looked);
  const link = address.replace(/^https?:\/\//, "");
  return (
    <Sheet
      open
      onClose={onClose}
      title={`Publish ${title}?`}
      sub={`It goes live at ${link}.`}
      footer={
        <>
          <Button onClick={onClose}>Not yet</Button>
          <Button variant="p" icon="send" disabled={blocks.length > 0 || host.publishState === "sending"} onClick={host.publish}>
            {host.publishState === "sending" ? "Publishing…" : "Publish"}
          </Button>
        </>
      }
    >
      <div className="mk-eyeb wine">{blocks.length ? "One thing first" : seen === WALK_STEPS.length ? "Every step looks right" : `${seen} of ${WALK_STEPS.length} steps looked at`}</div>
      <ul className="mk-checks">
        {blocks.map((b) => (
          <li key={b}>
            <span className="o">
              <Icon name="info" />
            </span>
            <b>{b}</b>
            {/offer/i.test(b) ? (
              <button type="button" className="k-link" onClick={onGoOffer}>
                Go to the offer
              </button>
            ) : null}
          </li>
        ))}
        {WALK_STEPS.map((s, i) => (
          <li key={s} className={looked.has(s) ? "ok" : ""}>
            <span className="o">
              <Icon name={looked.has(s) ? "check" : "eye"} />
            </span>
            <b>
              {i + 1} · {STEP_NAMES[s]}
            </b>
            {looked.has(s) ? null : (
              <button type="button" className="k-link" onClick={() => onGo(s)}>
                Look now
              </button>
            )}
          </li>
        ))}
        {waiting ? (
          <li>
            <span className="o">
              <Icon name="mail" />
            </span>
            <b>
              {waiting} {waiting === 1 ? "email waits" : "emails wait"} for your email
            </b>
            {canEdit ? (
              <Link className="k-link" to="/setup/email">
                Set up email
              </Link>
            ) : null}
          </li>
        ) : null}
      </ul>
      {host.publishError ? (
        <p className="note warn" role="alert">
          <Icon name="info" />
          <span>{host.publishError}</span>
        </p>
      ) : null}
    </Sheet>
  );
}

/** The published page, full screen, in the guest's own look. */
export function OpenPage({ address, doc, onClose }: { address: string; doc: string; onClose: () => void }) {
  return createPortal(
    <div className="mk-full" role="dialog" aria-label="The page">
      <div className="mk-fbar">
        <span className="mono">{address.replace(/^https?:\/\//, "")}</span>
        <button type="button" className="k-ib" aria-label="Close" onClick={onClose}>
          <Icon name="x" />
        </button>
      </div>
      <iframe className="mk-fframe" title="The page, full screen" srcDoc={doc} sandbox="" />
    </div>,
    document.body,
  );
}
